import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { InternalLayout, PageHeader } from "@/components/InternalLayout";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowLeftIcon as ArrowLeft, FloppyDiskIcon as FloppyDisk } from "@phosphor-icons/react";

const STATI = [{ v: "non_avviato", l: "Non avviato" }, { v: "in_corso", l: "In corso" }, { v: "attuato", l: "Attuato" }];

export default function Iso27001Control() {
  const { cid, code } = useParams();
  const nav = useNavigate();
  const [soa, setSoa] = useState(null);
  const [ctrl, setCtrl] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(`/iso27001/soa/${cid}`).then(({ data }) => {
      setSoa(data);
      const c = data.controls.find((x) => x.code === code);
      setCtrl(c ? { ...c, gestione: c.gestione || "", giustificazione: c.giustificazione || "" } : null);
    }).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  }, [cid, code]);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/iso27001/soa/${cid}/controls/${code}`, { applicabile: ctrl.applicabile, stato: ctrl.stato, giustificazione: ctrl.giustificazione || "", gestione: ctrl.gestione || "" });
      toast.success("Controllo aggiornato");
      nav(`/tools/iso27001?cliente=${cid}`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  if (!ctrl) return <InternalLayout><div className="p-8 text-sm text-muted-foreground">Caricamento…</div></InternalLayout>;
  const themeLabel = soa?.themes?.[ctrl.theme]?.it || ctrl.theme;

  return (
    <InternalLayout>
      <div className="p-8 lg:p-12 max-w-4xl">
        <button onClick={() => nav(-1)} className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mb-4" data-testid="iso-ctrl-back"><ArrowLeft size={15} /> Indietro</button>
        <PageHeader title={`A.${ctrl.code} — ${ctrl.title_it}`} subtitle={`${themeLabel} · ${soa?.cliente_nome || ""}`}
          actions={<Button onClick={save} disabled={saving} className="rounded-sm gap-2" data-testid="iso-ctrl-save"><FloppyDisk size={16} /> {saving ? "Salvataggio…" : "Salva"}</Button>} />
        <Card className="rounded-sm border-border shadow-none p-6 space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center justify-between border border-border rounded-sm px-4 py-3">
              <span className="text-sm">Applicabile</span>
              <Switch checked={!!ctrl.applicabile} onCheckedChange={(v) => setCtrl((c) => ({ ...c, applicabile: v }))} data-testid="iso-ctrl-app" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Stato di attuazione</Label>
              <Select value={ctrl.stato} onValueChange={(v) => setCtrl((c) => ({ ...c, stato: v }))} disabled={!ctrl.applicabile}>
                <SelectTrigger className="mt-1 rounded-sm" data-testid="iso-ctrl-stato"><SelectValue /></SelectTrigger>
                <SelectContent>{STATI.map((s) => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Giustificazione / riferimento (breve)</Label>
            <Input value={ctrl.giustificazione} onChange={(e) => setCtrl((c) => ({ ...c, giustificazione: e.target.value }))} className="mt-1 rounded-sm" data-testid="iso-ctrl-note" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Come viene gestito il controllo</Label>
            <p className="text-xs text-muted-foreground mt-0.5 mb-1">Descrizione estesa delle modalità di gestione/attuazione (policy, procedure, responsabili, strumenti, evidenze). Comparirà nel PDF della SoA. Non modificabile dal cliente.</p>
            <Textarea value={ctrl.gestione} onChange={(e) => setCtrl((c) => ({ ...c, gestione: e.target.value }))} rows={14} className="rounded-sm" placeholder="Descrivi come questo controllo è gestito e attuato…" data-testid="iso-ctrl-gestione" />
          </div>
        </Card>
      </div>
    </InternalLayout>
  );
}
